import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import cm from 'classnames';
import { Button } from 'ksnm-common-ui/lib/Button';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Tree } from 'ksnm-common-ui/lib/Tree';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { useCheckFromDictionaryCard } from '@src/utils/hooks/useCheckFromDictionaryCard';
import { usePermission } from '@src/utils/hooks/usePermission';
import { recursiveFindByCode } from '@src/utils/tree';
import { reportGroupsStore } from '@stores/dictionaryStores/reportGroupsStore/reportGroupsStore';
import { reportPeriodsStore } from '@stores/dictionaryStores/reportPeriodsStore';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { useAuditView } from '../../hooks/useAuditView';
import { Filter } from './Filter';
import { Table } from './Table';
import { toTreeView } from './toTreeView';

import styles from './ReportPeriods.module.scss';

export const ReportPeriods = observer(() => {
  const { reportGroups } = reportGroupsStore;
  const {
    fetch,
    reportPeriods,
    filters,
    clear,
    selectedKeys,
    expandedKeys,
    selectNode,
    fullSize,
    setSelectNode,
    setBreadcrumbs,
    setExpandedKeys,
    setSelectedKeys,
  } = reportPeriodsStore;
  const { push } = useHistory();

  const permission = usePermission('CmReportPeriods');

  const isBack = useCheckFromDictionaryCard(RoutePath.DICTIONARY_REPORT_PERIODS);

  useAuditView('Календарь отчетных форм');

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    if (!isBack) clear();
  }, [clear, isBack]);

  const treeData = useMemo(
    () =>
      reportGroups.length > 0
        ? toTreeView(
            reportPeriods.filter((report) => !(filters.reportGroup && report.reportGroupCode !== filters.reportGroup)),
          )
        : [],
    [filters.reportGroup, reportGroups.length, reportPeriods],
  );

  const onSelect = useCallback(
    (_, { node }) => {
      if (node.reportGroupCode) {
        setSelectNode(node);
        setSelectedKeys([node.key]);

        const nodes = recursiveFindByCode(node.key, treeData);
        if (nodes) setBreadcrumbs(nodes.map((item) => item.title));
      }
    },
    [setBreadcrumbs, setSelectNode, setSelectedKeys, treeData],
  );

  const onExpand = useCallback(
    (keys) => {
      setExpandedKeys(keys);
    },
    [setExpandedKeys],
  );

  const onCreate = useCallback(() => {
    push(`${RoutePath.DICTIONARY_REPORT_PERIODS}/${NEW_ENTITY_ID}`);
  }, [push]);

  const actions = useMemo(
    () =>
      !selectNode && permission.CREATE ? (
        <Button onClick={onCreate} tooltip="Добавить">
          <Icon name="add" />
        </Button>
      ) : null,
    [onCreate, permission, selectNode],
  );

  return (
    <DesignLayout scrollEnable>
      <Filter />
      <div className={styles.container}>
        <Tree
          selectable
          treeData={treeData}
          onSelect={onSelect}
          className={cm(styles.tree, {
            [styles.withTable]: selectNode,
            [styles.fullSize]: fullSize,
          })}
          selectedKeys={selectedKeys}
          expandedKeys={expandedKeys}
          onExpand={onExpand}
          actions={actions}
          showExpandButton={false}
        />
        <Table />
      </div>
    </DesignLayout>
  );
});
